import {
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
  type PageTheme,
} from '@backstage/theme';
import { asg } from './tokens';

// Every entity/page kind gets the same navy burst. Backstage ships nine of these
// in nine unrelated hues; one company has one header.
const navyPage = genPageTheme({
  colors: [asg.navy, asg.navy700],
  shape: shapes.wave,
});
const pageTheme: Record<string, PageTheme> = Object.fromEntries(
  [
    'home',
    'documentation',
    'tool',
    'service',
    'website',
    'library',
    'other',
    'app',
    'apis',
    'card',
  ].map(id => [id, navyPage]),
);

const typography = {
  fontFamily: asg.fontBody,
  htmlFontSize: 16,
  h1: { fontFamily: asg.fontHeading, fontSize: 54, fontWeight: 700, marginBottom: 10 },
  h2: { fontFamily: asg.fontHeading, fontSize: 40, fontWeight: 700, marginBottom: 8 },
  h3: { fontFamily: asg.fontHeading, fontSize: 32, fontWeight: 700, marginBottom: 6 },
  h4: { fontFamily: asg.fontHeading, fontSize: 28, fontWeight: 700, marginBottom: 6 },
  h5: { fontFamily: asg.fontHeading, fontSize: 24, fontWeight: 700, marginBottom: 4 },
  h6: { fontFamily: asg.fontHeading, fontSize: 20, fontWeight: 700, marginBottom: 2 },
};

// Shape tokens are part of the identity too - 16px cards on a navy-tinted
// shadow, pill buttons - so they are applied rather than only transcribed.
const components = {
  MuiButton: {
    styleOverrides: { root: { borderRadius: asg.radiusPill, fontWeight: 600 } },
  },
  MuiCard: {
    styleOverrides: {
      root: { borderRadius: asg.radiusLg, boxShadow: asg.shadowCard },
    },
  },
  MuiChip: {
    styleOverrides: { root: { borderRadius: asg.radiusSm } },
  },
};

// Sidebar. The knockout logo in modules/nav assumes this stays a dark navy.
const navigation = {
  background: asg.navy,
  indicator: asg.orange,
  color: asg.onNavyMuted,
  selectedColor: asg.white,
  navItem: { hoverBackground: asg.navy700 },
  submenu: { background: asg.navy700 },
};

export const asgLightTheme = createUnifiedTheme({
  fontFamily: asg.fontBody,
  defaultPageTheme: 'home',
  pageTheme,
  typography,
  components,
  palette: {
    ...palettes.light,
    primary: { main: asg.navy, contrastText: asg.white },
    secondary: { main: asg.green, contrastText: asg.white },
    error: { main: asg.danger },
    warning: { main: asg.warning },
    success: { main: asg.green },
    info: { main: asg.navy },
    background: { default: asg.surface, paper: asg.white },
    text: { primary: asg.body, secondary: asg.muted },
    // Brand rule: green fails WCAG AA for small body text on white, so body
    // copy is navy/ink and green is confined to status and fills.
    textContrast: asg.ink,
    textSubtle: asg.muted,
    textVerySubtle: asg.border,
    border: asg.border,
    // Brand-declared link colour (branding.yml `link`). Measured 4.15:1 on
    // white - AA for large text, marginally short of AA for 14px body. Raised
    // with the brand owner rather than silently substituted here; hover falls
    // back to navy, which is 14.6:1.
    link: asg.orange,
    linkHover: asg.navy,
    status: {
      ok: asg.green,
      warning: asg.warning,
      error: asg.danger,
      running: asg.navy,
      pending: asg.warning,
      aborted: asg.muted,
    },
    navigation,
    tabbar: { indicator: asg.orange },
  },
});

export const asgDarkTheme = createUnifiedTheme({
  fontFamily: asg.fontBody,
  defaultPageTheme: 'home',
  pageTheme,
  typography,
  components,
  palette: {
    ...palettes.dark,
    primary: { main: asg.orange, contrastText: asg.white },
    secondary: { main: asg.green, contrastText: asg.white },
    error: { main: asg.danger },
    warning: { main: asg.warning },
    success: { main: asg.green },
    info: { main: asg.onNavyMuted },
    background: { default: asg.navy700, paper: asg.navy },
    text: { primary: asg.white, secondary: asg.onNavyMuted },
    textContrast: asg.white,
    textSubtle: asg.onNavyMuted,
    border: asg.navy700,
    // Orange is only 3.4:1 on the navy ground - fine for a 4px indicator, not
    // for link text. Links stay white here and reveal the accent on hover.
    link: asg.white,
    linkHover: asg.orange,
    status: {
      ok: asg.green,
      warning: asg.warning,
      error: asg.danger,
      running: asg.onNavyMuted,
      pending: asg.warning,
      aborted: asg.muted,
    },
    navigation,
    tabbar: { indicator: asg.orange },
  },
});
